// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * On-device event store. Captured snapshots and clips are kept in IndexedDB on
 * this device only — never uploaded, and wiped in one click. If IndexedDB is
 * unavailable the app degrades to holding events in memory for the session.
 */

import type { CaptureEvent } from './types';

const DB_NAME = 'sentrywell';
const STORE = 'events';
let dbPromise: Promise<IDBDatabase> | null = null;
const memoryFallback: CaptureEvent[] = [];
let useMemory = false;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('no indexeddb'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb open failed'));
  });
  return dbPromise;
}

export async function saveEvent(ev: CaptureEvent): Promise<void> {
  try {
    const db = await openDb();
    await tx(db, 'readwrite', (store) => store.put(ev));
  } catch {
    useMemory = true;
    memoryFallback.push(ev);
  }
}

export async function loadEvents(): Promise<CaptureEvent[]> {
  if (useMemory) return [...memoryFallback].sort((a, b) => a.ts - b.ts);
  try {
    const db = await openDb();
    const all = await getAll(db);
    return all.sort((a, b) => a.ts - b.ts);
  } catch {
    useMemory = true;
    return [...memoryFallback].sort((a, b) => a.ts - b.ts);
  }
}

export async function deleteEvent(id: string): Promise<void> {
  if (useMemory) {
    const i = memoryFallback.findIndex((e) => e.id === id);
    if (i >= 0) memoryFallback.splice(i, 1);
    return;
  }
  try {
    const db = await openDb();
    await tx(db, 'readwrite', (store) => store.delete(id));
  } catch {
    /* ignore */
  }
}

export async function clearEvents(): Promise<void> {
  memoryFallback.length = 0;
  if (useMemory) return;
  try {
    const db = await openDb();
    await tx(db, 'readwrite', (store) => store.clear());
  } catch {
    /* ignore */
  }
}

function tx(db: IDBDatabase, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    fn(store);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error ?? new Error('tx failed'));
    t.onabort = () => reject(t.error ?? new Error('tx aborted'));
  });
}

function getAll(db: IDBDatabase): Promise<CaptureEvent[]> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, 'readonly');
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as CaptureEvent[]);
    req.onerror = () => reject(req.error ?? new Error('getAll failed'));
  });
}
